import { useEffect, useRef, useState } from 'react';

import classes from './Bubble.module.css';
import type { BubbleKind, BubbleRole } from './bubble-types.ts';
import { Bezel } from '#plugin-chat/components/bezel/Bezel.tsx';

export interface BubbleProps {
  role: BubbleRole;
  kind?: BubbleKind;
  children: React.ReactNode;
}

/** Animated chat message bubble with role-based styling and optional visual variants. */
export function Bubble({ kind, role, children }: BubbleProps) {
  const isUser = role === 'user';

  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const bevel = getComputedStyle(ref.current).getPropertyValue('--bezel-bevel');
    const radius = getComputedStyle(ref.current).getPropertyValue('--bezel-radius');
    const hasRadius = radius.split(/\s+/).some((r) => Number.parseInt(r, 10) !== 0);
    const shape = Array.from({ length: 4 }, (_) => `calc(${hasRadius ? radius : bevel} * 0.75)`);
    if (isUser) {
      shape[2] = '0px';
    } else {
      shape[3] = '0px';
    }
    const shapeRule = shape.join(' ');
    const key = `--bezel-${hasRadius ? 'radius' : 'bevel'}`;
    setStyle({
      [key]: shapeRule,
    });
  }, [isUser]);

  const color = {
    default: `var(--chat-color-role-${role})`,
    error: 'var(--chat-color-kind-error, var(--mantine-color-red-5))',
    success: 'var(--chat-color-kind-success, var(--mantine-color-green-2))',
    subtle: 'var(--chat-color-kind-subtle, var(--mantine-color-dark-3))',
  }[kind ?? 'default'];

  return (
    <div className={classes.bubble} ref={ref}>
      <Bezel primaryColor={color} style={style}>
        <Bezel.Effects>
          <Bezel.Border stream="idle" />
          {!isUser && <Bezel.Corners opacity={[1, 0, 1, 0]} />}
          <Bezel.Glint />
          {isUser && !kind && <Bezel.Glow blur={10} intensity={0.3} />}
          {kind && <Bezel.Glow blur={20} intensity={0.15} />}
          {isUser && <Bezel.Background intensity={1} />}
        </Bezel.Effects>
        <div className={classes.content}>{children}</div>
      </Bezel>
    </div>
  );
}
